import React from "react";

const AcceptedArticleTypes = () => {
  return (
    <React.Fragment>
      <h1>Accepted Article Types</h1>
      <p>
        Cureus accepts and publishes six different article types: Original Article, Review Article,
        Technical Report, Case Study, Editorials, and Letters. To be considered for publication,
        each article type must include the sections listed below. Only original articles may include
        abstract subheadings. CJoE does not accept or publish article commentaries or letters to the
        editor. Please note that all articles must undergo peer review before publication.
      </p>

      <h2>
        <strong>Free Publication Eligibility</strong>
      </h2>
      <p>
        Original articles, Case Studies, Technical Reports, Editorials, and Letters that exceed the
        specified number of authors or references will no longer be eligible for free publication.
        All review articles require the purchase of{" "}
        <a href="/cureus_editing_services" target="_blank" rel="noopener">
          Preferred Editing
        </a>{" "}
        regardless of the number of authors or references. Please note that staying within the
        limits does not guarantee free publication, as articles with too many errors will still
        require{" "}
        <a href="/cureus_editing_services" target="_blank" rel="noopener">
          Preferred Editing
        </a>
        .
      </p>

      <h2 className="space-top-m">
        <strong>Original Article</strong>
      </h2>
      <div className="article-type-info row">
        <div className="medium-offset-0 medium-8 columns">
          <p>
            Original articles are original research studies classified as primary literature. An
            original article is the most common type of journal manuscript used to publish full
            reports of data from research. Original articles may also be referred to as research
            articles, research, or just articles, as this format is suitable for many different
            fields and different types of studies. Authors can add limitations and scope for future
            study in the Results and Discussion section under a separate subheading.
          </p>
        </div>
        <div className="small-offset-5 small-6 medium-offset-1 medium-3 columns sections">
          <div className="small-font blue">
            <strong>SECTIONS</strong>
          </div>
          <div className="article-section-list">
            <div>Abstract</div>
            <div>Introduction</div>
            <div>Materials and Methods</div>
            <div>Results and Discussion</div>
            <div>Conclusion</div>
            <div>References</div>
          </div>
        </div>
        <div className="small-12 columns centered">
          <div className="small-font blue space-bottom-s">
            <strong>Free Original Articles Limited To</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-2 medium-4 columns centered">
          <div className="large-font .space-bottom-xxs">
            <strong>30</strong>
          </div>
          <div className="small-font">
            <strong>Authors</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-0 medium-4 columns centered end">
          <div className="large-font .space-bottom-xxs">
            <strong>200</strong>
          </div>
          <div className="small-font">
            <strong>References</strong>
          </div>
        </div>
      </div>

      <h2 className="space-top-xxl">
        <strong>Review Article</strong>
      </h2>

      <div className="article-type-info row">
        <div className="medium-offset-0 medium-8 columns">
          <p>
            Review articles give an overview of existing literature in a field, often identifying
            specific problems or issues and providing a balanced analysis derived from available
            published work on the topic. Review articles provide a comprehensive summary of research
            on the selected topic, and a perspective on the state of the field and where it is
            heading.
          </p>
          <p>
            Review articles can be one of two types, broadly speaking: literature reviews or
            systematic reviews. Bibliometric analysis and meta-analysis articles should also be
            submitted as review articles. Authors can add limitations and scope for future study in
            the discussion section under a separate subheading.
          </p>
        </div>
        <div className="small-offset-5 small-6 medium-offset-1 medium-3 columns sections">
          <div className="small-font blue">
            <strong>SECTIONS</strong>
          </div>
          <div className="article-section-list">
            <div>Abstract</div>
            <div>Introduction and Background</div>
            <div>Review</div>
            <div>Conclusions</div>
            <div>References</div>
          </div>
        </div>
        <div className="small-12 columns centered">
          <div className="small-font blue space-bottom-s">
            <strong>Preferred Editing Required for All Review Articles</strong>
          </div>
        </div>
      </div>

      <h2 className="space-top-xxl">
        <strong>Case Study</strong>
      </h2>
      <div className="article-type-info row">
        <div className="medium-offset-0 medium-8 columns">
          <p>
            Engineering case studies provide detailed accounts of real-world engineering projects or
            challenges. These reports contribute significantly to the existing knowledge in the
            field and may highlight unconventional results. An engineering case study should
            thoroughly examine the project's specifications, challenges encountered, engineering
            diagnosis, and applied solutions. The primary objective is to inform other professionals
            about potential challenges they may face in similar situations. Reports involving five
            or fewer cases should be submitted in the case study format. If the report includes
            images from tools such as blueprints, schematics, simulations, or prototypes, they
            should be included unless they do not add relevant information. Submissions that combine
            a case presentation with a literature review should be categorized as engineering case
            studies. Additionally, detailed author contributions must be acknowledged, particularly
            when the case study has more than five authors.
          </p>
        </div>
        <div className="small-offset-5 small-6 medium-offset-1 medium-3 columns sections">
          <div className="small-font blue">
            <strong>SECTIONS</strong>
          </div>
          <div className="article-section-list">
            <div>Abstract</div>
            <div>Introduction</div>
            <div>Case Description</div>
            <div>Discussion</div>
            <div>Conclusions</div>
            <div>References</div>
          </div>
        </div>
        <div className="small-12 columns centered">
          <div className="small-font blue space-bottom-s">
            <strong>Free case studies Limited To</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-2 medium-4 columns centered">
          <div className="large-font space-bottom-xxs">
            <strong>30</strong>
          </div>
          <div className="small-font">
            <strong>Authors</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-0 medium-4 columns centered end">
          <div className="large-font space-bottom-xxs">
            <strong>200</strong>
          </div>
          <div className="small-font">
            <strong>References</strong>
          </div>
        </div>
      </div>

      <h2 className="space-top-xxl">
        <strong>Letters</strong>
      </h2>
      <div className="article-type-info row">
        <div className="medium-offset-0 medium-8 columns">
          <p>
            Letters are a concise format of research articles designed for the rapid communication
            of novel and significant findings. These submissions are intended for cases where
            immediate attention is warranted due to their relevance and originality. While Letters
            follow the same general structure as original research articles, they differ primarily
            in length and the number of references. Specifically, they are more succinct, with a
            maximum limit of 200 references. This format enables the swift publication of emerging
            research while maintaining the quality and depth of the content.
          </p>
        </div>
        <div className="small-offset-5 small-6 medium-offset-1 medium-3 columns sections">
          <div className="small-font blue">
            <strong>SECTIONS</strong>
          </div>
          <div className="article-section-list">
            <div>Abstract</div>
            <div>Introduction</div>
            <div>Materials and Methods</div>
            <div>Results and Discussion</div>
            <div>Conclusion</div>
            <div>References</div>
          </div>
        </div>
        <div className="small-12 columns centered">
          <div className="small-font blue space-bottom-s">
            <strong>Free Letters Limited To</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-2 medium-4 columns centered">
          <div className="large-font space-bottom-xxs">
            <strong>30</strong>
          </div>
          <div className="small-font">
            <strong>Authors</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-0 medium-4 columns centered end">
          <div className="large-font space-bottom-xxs">
            <strong>200</strong>
          </div>
          <div className="small-font">
            <strong>References</strong>
          </div>
        </div>
      </div>

      <h2 className="space-top-xxl">
        <strong>Technical Report</strong>
      </h2>
      <div className="article-type-info row">
        <div className="medium-offset-0 medium-8 columns">
          <p>
            Technical reports are formal reports designed to convey technical information in a clear
            and easily accessible format. They are typically divided into sections which allow
            different readers to access different levels of information. A technical report (also a
            scientific report) is a document that describes the process, progress, or results of
            technical or scientific research or the state of a technical or scientific research
            problem. It might also include recommendations and conclusions of the research.
          </p>
        </div>
        <div className="small-offset-5 small-6 medium-offset-1 medium-3 columns sections">
          <div className="small-font blue">
            <strong>SECTIONS</strong>
          </div>
          <div className="article-section-list">
            <div>Abstract</div>
            <div>Introduction</div>
            <div>Technical Report</div>
            <div>Discussion</div>
            <div>Conclusions</div>
            <div>References</div>
          </div>
        </div>
        <div className="small-12 columns centered">
          <div className="small-font blue space-bottom-s">
            <strong>Free Technical Reports Limited To</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-2 medium-4 columns centered">
          <div className="large-font space-bottom-xxs">
            <strong>30</strong>
          </div>
          <div className="small-font">
            <strong>Authors</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-0 medium-4 columns centered end">
          <div className="large-font space-bottom-xxs">
            <strong>200</strong>
          </div>
          <div className="small-font">
            <strong>References</strong>
          </div>
        </div>
      </div>

      <h2 className="space-top-xxl">
        <strong>Editorial</strong>
      </h2>
      <div className="article-type-info row">
        <div className="medium-offset-0 medium-8 columns">
          <p>
            Editorials are opinion or perspective pieces on a relevant, timely topic. Editorials
            will only be approved if submitted by senior academicians or researchers with an
            established publishing history.
          </p>
        </div>
        <div className="small-offset-5 small-6 medium-offset-1 medium-3 columns sections">
          <div className="small-font blue">
            <strong>SECTIONS</strong>
          </div>
          <div className="article-section-list">
            <div>Abstract</div>
            <div>Editorial</div>
            <div>References</div>
          </div>
        </div>
        <div className="small-12 columns centered">
          <div className="small-font blue space-bottom-s">
            <strong>Free Editorials Limited To</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-2 medium-4 columns centered">
          <div className="large-font space-bottom-xxs">
            <strong>30</strong>
          </div>
          <div className="small-font">
            <strong>Authors</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-0 medium-4 columns centered end">
          <div className="large-font space-bottom-xxs">
            <strong>200</strong>
          </div>
          <div className="small-font">
            <strong>References</strong>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default AcceptedArticleTypes;
